import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { GroupService, InvitationService, InvitationWrite } from '../../../../../js/api';
import { useCurrentUser } from '../../../../../js/app/hooks/auth';
import { groupQueryKeys } from '../../../../../js/app/query-keys-factories/groups';
import { invitationQueryKeys } from '../../../../../js/app/query-keys-factories/invitations';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { setFormErrorsFromApiErrors } from '../../../../../js/utils/forms';
import { BrandSwitch } from '../../../components/BrandSwitch';
import { Button } from '../../../components/button';
import { Dialog, DialogBody, DialogTitle, DialogActions } from '../../../components/dialog';
import { DropdownSelect } from '../../../components/dropdown-select';
import { Field, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { Spinner } from '../../../components/spinner';
import { useCodeDefinitionsAsOptions } from '../../../hooks/useCodeDefinitions';
import { canOverrideRepGroups, getDefaultGroupsForRole, useRoleDefaults } from './roleDefaults';

const invitationValidation = z.object({
  email: z.string().email(),
  role: z.string().min(1, 'Role is required'),
  group_names: z.array(z.string()),
});

type InviteFormData = z.infer<typeof invitationValidation>;

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
}

export function InviteUserDialog({ open, onClose }: InviteUserDialogProps) {
  const { user: currentUser } = useCurrentUser();
  const canOverride = canOverrideRepGroups(currentUser);

  const form = useForm<InviteFormData>({
    resolver: zodResolver(invitationValidation),
    defaultValues: { email: '', role: '', group_names: [] },
  });

  const permissionGroups = useQuery({
    queryKey: groupQueryKeys.list({ limit: 500 }),
    queryFn: async () => {
      const data = await GroupService.groupList({ limit: 500 });
      return data.results || [];
    },
    keepPreviousData: true,
  });

  const { data: roleOptions = [], isLoading: rolesLoading } =
    useCodeDefinitionsAsOptions('HealthcareEntityRepRole');

  const { data: roleDefaultsMap } = useRoleDefaults();

  const applyRoleDefaults = (role: string) => {
    const apiGroupCodes = permissionGroups.data?.map((g) => g.code) ?? [];
    const defaults = getDefaultGroupsForRole(role, apiGroupCodes, roleDefaultsMap);
    form.setValue('group_names', defaults, { shouldDirty: true });
  };

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: InvitationWrite) => {
      await InvitationService.invitationCreate({ requestBody: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(invitationQueryKeys.all());
    },
  });

  const onSubmit = async (data: InviteFormData) => {
    try {
      await mutation.mutateAsync({
        email: data.email,
        role: data.role as InvitationWrite['role'],
        // Server enforces gate: only sent when the caller can override.
        ...(canOverride ? { group_names: data.group_names } : {}),
      } as InvitationWrite);
      toast.success('User invited successfully.');
      form.reset({ email: '', role: '', group_names: [] });
      onClose();
    } catch (errors) {
      setFormErrorsFromApiErrors(
        errors as Parameters<typeof setFormErrorsFromApiErrors>[0],
        form.setError
      );
      handleApiError(errors);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="4xl">
      <form onSubmit={form.handleSubmit(onSubmit)} data-testid="invite-user-dialog">
        <DialogTitle>Invite User</DialogTitle>
        <DialogBody>
          <div className="tw:space-y-6">
            <Field>
              <Label htmlFor="invite-email">
                Email
                <span className="tw:text-stoplight-danger">*</span>
              </Label>
              <Input
                id="invite-email"
                type="email"
                {...form.register('email')}
                className="tw:w-full"
                data-invalid={form.formState.errors.email ? true : undefined}
                data-testid="invite-user-dialog-email"
              />
              {form.formState.errors.email && (
                <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                  {form.formState.errors.email.message}
                </p>
              )}
            </Field>

            <Field>
              <Label htmlFor="invite-role">
                Role
                <span className="tw:text-stoplight-danger">*</span>
              </Label>
              <Controller
                name="role"
                control={form.control}
                render={({ field }) => (
                  <DropdownSelect
                    inputId="invite-role"
                    allOptions={roleOptions}
                    value={field.value}
                    onChange={(option) => {
                      const next = Array.isArray(option) ? option[0]?.value : option?.value;
                      const nextRole = next ?? '';
                      field.onChange(nextRole);
                      if (nextRole) applyRoleDefaults(nextRole);
                    }}
                    isLoading={rolesLoading}
                    placeholder="Select a role..."
                  />
                )}
              />
              {form.formState.errors.role && (
                <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                  {form.formState.errors.role.message}
                </p>
              )}
            </Field>

            <div>
              <p className="tw:mb-3 tw:mt-3 tw:font-semibold tw:text-zinc-950">
                {canOverride
                  ? 'Set the appropriate User Permissions'
                  : 'Default Permissions for this Role'}
              </p>

              {permissionGroups.isLoading && (
                <div className="tw:flex tw:items-center tw:justify-center tw:py-8">
                  <Spinner aria-label="loading indicator" />
                </div>
              )}

              {permissionGroups.isError && (
                <div className="tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-3 tw:text-red-700">
                  <p className="tw:font-semibold">Something went wrong</p>
                  <p className="tw:text-sm">We couldn&apos;t retrieve the list of permissions.</p>
                </div>
              )}

              {permissionGroups.isSuccess && permissionGroups.data && (
                <Controller
                  name="group_names"
                  control={form.control}
                  render={({ field }) => (
                    <div className="tw:space-y-4">
                      {permissionGroups.data.map((group) => {
                        const selected = Array.isArray(field.value)
                          ? field.value.includes(group.code)
                          : false;
                        return (
                          <div key={group.code} className="tw:flex tw:gap-4 tw:items-start">
                            <BrandSwitch
                              className="tw:!mt-1"
                              checked={selected}
                              disabled={!canOverride}
                              onChange={(checked) => {
                                if (!canOverride) return;
                                const current = Array.isArray(field.value) ? field.value : [];
                                if (checked) {
                                  field.onChange([...current, group.code]);
                                } else {
                                  field.onChange(current.filter((c) => c !== group.code));
                                }
                              }}
                            />
                            <div className="tw:flex tw:flex-col tw:gap-0.5 tw:min-w-0">
                              <span className="tw:font-medium">{group.title}</span>
                              {group.description && (
                                <span className="tw:text-xs tw:text-zinc-500">
                                  {group.description}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                />
              )}
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button type="button" plain onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="brandgreen1"
            disabled={mutation.isPending || permissionGroups.isLoading || rolesLoading}
            data-testid="invite-user-dialog-submit"
          >
            {mutation.isPending ? 'Inviting...' : 'Invite'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
